import type { Curriculum } from "./curriculum";
import type { SchoolLevel } from "./catalog";

export type AkyoTaxonomyKind = "level" | "subject" | "theme" | "chapter";
export type AkyoTaxonomyNode = {
  id: string;
  kind: AkyoTaxonomyKind;
  code: string;
  label: string;
  revision: number;
  archived: boolean;
  levelId?: string;
  subjectId?: string;
  themeId?: string;
};
export type AkyoTaxonomySnapshot = {
  schemaVersion: 1;
  revision: number;
  nodes: AkyoTaxonomyNode[];
};

export type AkyoNavigation = {
  id: string; code: string; label: string;
  subjects: {id: string; code: string; label: string; schoolLevel: SchoolLevel}[];
}[];

/** Live identities/labels for navigation; historical keys are only the storage bridge. */
export function projectAkyoNavigation(value: unknown): AkyoNavigation {
  const snapshot = parseAkyoTaxonomy(value);
  const active = snapshot.nodes.filter(n => !n.archived);
  const levels = new Map(active.filter(n=>n.kind==='level').map(n=>[n.code,n]));
  const subjects = new Map(active.filter(n=>n.kind==='subject').map(n=>[n.code,n]));
  const canonical = snapshot.nodes.some(n=>n.kind==='subject'&&n.code==='physique-chimie-spe');
  const result: AkyoNavigation = [];
  for (const route of routes) {
    const [levelCode,subjectCode] = !canonical && route.legacy ? route.legacy : [route.level,route.subject];
    const sourceLevel = levels.get(levelCode), subject = subjects.get(subjectCode);
    if (!sourceLevel || !subject) continue;
    const level = levels.get(route.level) ?? sourceLevel;
    let entry = result.find(n=>n.id===level.id);
    if (!entry) {
      entry={id:level.id,code:level.code,label:!canonical && route.level==='1re-spe'?'Première':!canonical && route.level==='tle-spe'?'Terminale':level.label,subjects:[]};
      result.push(entry);
    }
    entry.subjects.push({id:subject.id,code:subject.code,label:subject.label,schoolLevel:route.key});
  }
  return result;
}

// Verified Akyo codes (r91/r108). Labels are presentation, never join keys.
const routes: { key: SchoolLevel; level: string; subject: string; legacy?: [string, string] }[] = [
  ...["6e", "5e", "4e", "3e", "Seconde", "Post-bac"].map(key => ({key: key as SchoolLevel, level: key.toLowerCase(), subject: "physique-chimie"})),
  {key: "1re Spé", level: "1re-spe", subject: "physique-chimie-spe", legacy: ["1re-spe", "physique-chimie"]},
  {key: "Tle Spé", level: "tle-spe", subject: "physique-chimie-spe", legacy: ["tle-spe", "physique-chimie"]},
  {key: "1re ES", level: "1re-spe", subject: "enseignement-scientifique", legacy: ["1re-es", "enseignement-scientifique"]},
  {key: "Tle ES", level: "tle-spe", subject: "enseignement-scientifique", legacy: ["tle-es", "enseignement-scientifique"]},
];

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function text(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}
function node(value: unknown): value is AkyoTaxonomyNode {
  if (!record(value) || !text(value.id) || !text(value.code) || !text(value.label)) return false;
  if (!["level","subject","theme","chapter"].includes(String(value.kind))) return false;
  if (typeof value.revision !== "number" || !Number.isInteger(value.revision) || value.revision < 1) return false;
  if (typeof value.archived !== "boolean") return false;
  if (value.kind === "theme") return text(value.levelId) && text(value.subjectId);
  if (value.kind === "chapter") return text(value.themeId);
  return true;
}

export function parseAkyoTaxonomy(value: unknown): AkyoTaxonomySnapshot {
  if (!record(value) || value.schemaVersion !== 1 || typeof value.revision !== "number" || !Number.isInteger(value.revision) || value.revision < 0 || !Array.isArray(value.nodes)) {
    throw new Error("invalid_akyo_taxonomy");
  }
  if (!value.nodes.every(node)) throw new Error("invalid_akyo_taxonomy");
  const nodes = value.nodes as AkyoTaxonomyNode[];
  if (new Set(nodes.map(item => item.id)).size !== nodes.length) throw new Error("invalid_akyo_taxonomy");
  // Akyo codes are unique within their parents, not across the whole catalog.
  const codeKeys = nodes.map(item => JSON.stringify(item.kind === 'theme'
    ? [item.kind, item.levelId, item.subjectId, item.code]
    : item.kind === 'chapter' ? [item.kind, item.themeId, item.code] : [item.kind, item.code]));
  if (new Set(codeKeys).size !== nodes.length) throw new Error("ambiguous_akyo_taxonomy");
  const byId = new Map(nodes.map(item => [item.id, item]));
  for (const item of nodes) {
    if (item.kind === 'theme' && (byId.get(item.levelId!)?.kind !== 'level' || byId.get(item.subjectId!)?.kind !== 'subject')) throw new Error('invalid_akyo_parents');
    if (item.kind === 'chapter' && byId.get(item.themeId!)?.kind !== 'theme') throw new Error('invalid_akyo_parents');
  }
  return { schemaVersion: 1, revision: value.revision, nodes };
}

export type AkyoClassification = {levelId: string; subjectId: string; themeId: string; chapterId: string};

function chapterPath(snapshot: AkyoTaxonomySnapshot, id: string) {
  const byId = new Map(snapshot.nodes.map(n => [n.id, n]));
  const chapter = byId.get(id);
  const theme = byId.get(chapter?.themeId ?? '');
  const level = byId.get(theme?.levelId ?? '');
  const subject = byId.get(theme?.subjectId ?? '');
  if (chapter?.kind !== 'chapter' || theme?.kind !== 'theme' || level?.kind !== 'level' || subject?.kind !== 'subject' || [chapter,theme,level,subject].some(n => n.archived)) throw new Error('invalid_akyo_path');
  const route = routes.find(r => (r.level === level.code && r.subject === subject.code) || (r.legacy?.[0] === level.code && r.legacy?.[1] === subject.code));
  return {chapter, route, path: {levelId:level.id,subjectId:subject.id,themeId:theme.id,chapterId:chapter.id}};
}

/** Explicit read-time reconciliation; never writes historical records. An old
 * snapshot is required to prove the stored parents and the subject identity. */
export function reconcileAkyoClassification(current: unknown, stored: AkyoClassification, previous?: unknown) {
  const next = chapterPath(parseAkyoTaxonomy(current), stored.chapterId);
  const same = (a: AkyoClassification, b: AkyoClassification) => (Object.keys(a) as (keyof AkyoClassification)[]).every(k => a[k] === b[k]);
  if (same(next.path, stored)) return {path: next.path, repaired: false};
  if (!previous) throw new Error('akyo_path_requires_previous_snapshot');
  const old = chapterPath(parseAkyoTaxonomy(previous), stored.chapterId);
  if (!same(old.path, stored) || !old.route || old.route.key !== next.route?.key || old.chapter.code !== next.chapter.code || old.path.themeId !== next.path.themeId) throw new Error('incoherent_akyo_reclassification');
  return {path: next.path, repaired: true};
}

export function projectAkyoTaxonomyToCurriculum(value: unknown): Curriculum {
  const snapshot = parseAkyoTaxonomy(value);
  const active = snapshot.nodes.filter(item => !item.archived);
  const byId = new Map(active.map(item => [item.id, item]));
  const subjectsByCode = new Map(active.filter(item => item.kind === "subject").map(item => [item.code, item]));
  const result: Curriculum = {};

  const levelsByCode = new Map(active.filter(item => item.kind === "level").map(item => [item.code, item]));
  const canonical = snapshot.nodes.some(n=>n.kind==='subject'&&n.code==='physique-chimie-spe');
  for (const route of routes) {
    const [levelCode, subjectCode] = !canonical && route.legacy ? route.legacy : [route.level, route.subject];
    const level = levelsByCode.get(levelCode);
    const subject = subjectsByCode.get(subjectCode);
    if (!level || !subject) continue;
    const themes = active
      .filter(item => item.kind === "theme" && item.levelId === level.id && item.subjectId === subject.id)
      .sort((a,b) => a.code.localeCompare(b.code, "fr", { numeric: true }))
      .map(theme => ({
        // Codes are the durable bridge with the old Competenceo curriculum.
        id: theme.code,
        title: theme.label,
        chapters: active
          .filter(item => item.kind === "chapter" && item.themeId === theme.id && byId.has(theme.id))
          .sort((a,b) => a.code.localeCompare(b.code, "fr", { numeric: true }))
          .map(chapter => ({ id: chapter.code, title: chapter.label })),
      }));
    result[route.key] = themes;
  }
  return result;
}
